import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { mockExpenses } from '@/mocks/tr/expenses';

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    'Ödendi': 'bg-green-50 text-green-600 border-green-200',
    'Ödenecek': 'bg-gold-50 text-gold-600 border-gold-200',
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${styles[status] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${status === 'Ödendi' ? 'bg-green-500' : 'bg-gold-500'}`} />
      {status}
    </span>
  );
}

export default function Expenses() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showModal, setShowModal] = useState(false);

  const summary = useMemo(() => {
    const total = mockExpenses.reduce((sum, e) => sum + e.amount, 0);
    const paid = mockExpenses.filter((e) => e.payment_status === 'Ödendi').reduce((sum, e) => sum + e.amount, 0);
    const pending = mockExpenses.filter((e) => e.payment_status === 'Ödenecek').reduce((sum, e) => sum + e.amount, 0);
    const upcoming = mockExpenses.filter((e) => e.payment_status === 'Ödenecek').length;
    return { total, paid, pending, upcoming };
  }, []);

  const filtered = useMemo(() => {
    return mockExpenses.filter((e) => {
      const matchSearch =
        e.title.toLowerCase().includes(search.toLowerCase()) ||
        e.description.toLowerCase().includes(search.toLowerCase()) ||
        e.category.includes(search);
      const matchCategory = !filterCategory || e.category === filterCategory;
      const matchStatus = !filterStatus || e.payment_status === filterStatus;
      return matchSearch && matchCategory && matchStatus;
    });
  }, [search, filterCategory, filterStatus]);

  const categories = ['Kira', 'Doğalgaz', 'Elektrik', 'Su', 'Aidat', 'Personel', 'Kargo', 'Reklam', 'Muhasebe', 'Vergi', 'Diğer'];

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold text-boutique-black">{t('expenses.title')}</h1>
          <p className="text-sm text-boutique-warm mt-1">Butiğin tüm giderlerini takip edin</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors whitespace-nowrap"
        >
          <i className="ri-add-line" />
          {t('expenses.newExpense')}
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center text-red-500">
              <i className="ri-bill-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">Bu Ay Toplam Gider</p>
              <p className="text-xl font-semibold text-red-600">{formatCurrency(summary.total)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center text-green-500">
              <i className="ri-check-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">Ödenmiş Giderler</p>
              <p className="text-xl font-semibold text-green-600">{formatCurrency(summary.paid)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gold-50 flex items-center justify-center text-gold-500">
              <i className="ri-time-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">Bekleyen Giderler</p>
              <p className="text-xl font-semibold text-gold-600">{formatCurrency(summary.pending)}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-cream-200 p-5">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center text-blue-500">
              <i className="ri-calendar-check-line text-lg" />
            </div>
            <div>
              <p className="text-xs font-medium text-boutique-warm uppercase">Yaklaşan Ödemeler</p>
              <p className="text-xl font-semibold text-blue-600">{summary.upcoming} adet</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 bg-white p-4 rounded-xl border border-cream-200">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-boutique-warm" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('common.search')}
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm"
          />
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm bg-white sm:w-40"
        >
          <option value="">Tüm Kategoriler</option>
          {categories.map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none transition-all text-sm bg-white sm:w-40"
        >
          <option value="">Tüm Durumlar</option>
          <option value="Ödendi">{t('common.paidStatus')}</option>
          <option value="Ödenecek">{t('common.unpaid')}</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-cream-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-cream-200">
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tarih</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Gider</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Kategori</th>
                <th className="text-right px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Tutar</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Durum</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Vade</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">Açıklama</th>
                <th className="text-left px-4 py-3 font-semibold text-boutique-black whitespace-nowrap">İşlemler</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-100">
              {filtered.map((e) => (
                <tr key={e.id} className="hover:bg-cream-50 transition-colors">
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{e.date}</td>
                  <td className="px-4 py-3 whitespace-nowrap font-medium text-boutique-black">{e.title}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-cream-100 text-boutique-warm border border-cream-200">
                      {e.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-boutique-black whitespace-nowrap">{formatCurrency(e.amount)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <StatusBadge status={e.payment_status} />
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-boutique-warm">{e.due_date}</td>
                  <td className="px-4 py-3 text-boutique-warm max-w-xs truncate">{e.description}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex items-center gap-1">
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-blue-500 hover:bg-blue-50 transition-colors" title={t('common.edit')}>
                        <i className="ri-edit-line text-base" />
                      </button>
                      <button className="w-8 h-8 rounded-lg flex items-center justify-center text-red-500 hover:bg-red-50 transition-colors" title={t('common.delete')}>
                        <i className="ri-delete-bin-line text-base" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="text-center py-12">
            <i className="ri-bill-line text-4xl text-cream-300 mb-3" />
            <p className="text-sm text-boutique-warm">{t('common.noData')}</p>
          </div>
        )}
      </div>

      {/* New Expense Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-cream-200 flex items-center justify-between">
              <h2 className="font-semibold text-boutique-black text-lg">{t('expenses.newExpense')}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 rounded-lg flex items-center justify-center text-boutique-warm hover:bg-cream-100 transition-colors">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Gider Başlığı</label>
                <input className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="Örn: Mağaza Kirası" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Kategori</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  {categories.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Tutar</label>
                  <input type="number" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" placeholder="0.00" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-boutique-black mb-2">Tarih</label>
                  <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Ödeme Durumu</label>
                <select className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm bg-white">
                  <option>Ödendi</option>
                  <option>Ödenecek</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Vade Tarihi</label>
                <input type="date" className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-boutique-black mb-2">Açıklama</label>
                <textarea className="w-full px-4 py-2.5 rounded-lg border border-cream-200 focus:border-gold-400 focus:ring-2 focus:ring-gold-100 outline-none text-sm resize-none h-20" placeholder="Ek açıklama..." />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 px-4 border border-cream-200 text-boutique-black rounded-lg text-sm font-medium hover:bg-cream-50 transition-colors">
                  {t('common.cancel')}
                </button>
                <button className="flex-1 py-2.5 px-4 bg-boutique-black text-white rounded-lg text-sm font-medium hover:bg-boutique-charcoal transition-colors">
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}